import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { useRoleRoutes } from '@/routing';
import type { GroupRoute, PageRoute, RouteConfig } from '@/routing/types';

function LogoHeader() {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          size="lg"
          className="cursor-pointer hover:bg-white"
          onClick={() => {
            navigate('/');
            if (isMobile) {
              setOpenMobile(false);
            }
          }}
        >
          <div className="flex aspect-square size-8 items-center justify-center rounded-lg">
            <img src="/goreeva_logo.png" alt="GOREEVA" className="w-full h-full object-contain" />
          </div>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">GOREEVA</span>
            <span className="truncate text-xs">School Management App</span>
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function SidebarSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4">
      <Skeleton className="h-8 w-full" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    </div>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { sidebarRoutes, isLoading } = useRoleRoutes();

  const location = useLocation();
  const navigate = useNavigate();

  const { isMobile, setOpenMobile } = useSidebar();
  const subjectId = sessionStorage.getItem('currentSubjectId');
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>({});

  // Check if a route is active
  const isRouteActive = React.useCallback(
    (path: string) => {
      const currentPath = location.pathname;

      // Handle dynamic routes with :subjectId
      if (path.includes(':subjectId')) {
        const pathPattern = path.replace(':subjectId', '[^/]+');
        const regex = new RegExp(`^${pathPattern}$`);
        return regex.test(currentPath);
      }

      // For non-dynamic routes, we want exact matches
      // This ensures that parent routes don't show as active when on child routes
      return currentPath === path;
    },
    [location.pathname]
  );

  // For group headers, we need to check if any child is active
  const isGroupActive = React.useCallback(
    (children: RouteConfig[]) => {
      return children.some((child) => {
        if ('path' in child) {
          return isRouteActive(child.path);
        }
        return false;
      });
    },
    [isRouteActive]
  );

  // Initialize open groups based on current path
  React.useEffect(() => {
    const newOpenGroups: Record<string, boolean> = {};

    // Find which group contains the current path
    sidebarRoutes.forEach((route) => {
      if ('isGroup' in route) {
        if (isGroupActive(route.children)) {
          newOpenGroups[route.name] = true;
        }
      }
    });

    setOpenGroups((prev) => ({
      ...prev,
      ...newOpenGroups,
    }));
  }, [sidebarRoutes, isGroupActive]);

  const toggleGroup = React.useCallback((groupName: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  }, []);

  // Common active and hover styles
  const activeClassName = React.useMemo(
    () => 'text-primary font-medium bg-primary/15 transition-all duration-200',
    []
  );

  const hoverClassName = React.useMemo(
    () => 'hover:bg-primary/10 transition-all duration-200 rounded-md',
    []
  );

  // Render functions for different route types
  const renderLabelRoute = React.useCallback((route: RouteConfig) => {
    if ('isLabel' in route) {
      return (
        <SidebarGroupLabel
          key={`label-${route.name}`}
          className="text-xs font-medium tracking-wider text-muted-foreground/70 px-2"
        >
          {route.name}
        </SidebarGroupLabel>
      );
    }
    return null;
  }, []);

  const renderGroupRoute = React.useCallback(
    (route: RouteConfig) => {
      if ('isGroup' in route) {
        const isOpen = openGroups[route.name] || false;
        const hasActiveChild = isGroupActive(route.children);

        return (
          <Collapsible
            key={`group-${route.name}`}
            asChild
            open={isOpen}
            onOpenChange={() => toggleGroup(route.name)}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  tooltip={route.name}
                  className={`${hoverClassName} ${
                    hasActiveChild ? activeClassName : ''
                  } px-3 py-2 text-[15px]`}
                >
                  {route.icon && (
                    <route.icon
                      className={`h-4 w-4 ${
                        hasActiveChild ? 'text-primary' : 'text-muted-foreground'
                      }`}
                    />
                  )}
                  <span className={hasActiveChild ? 'text-primary' : 'text-muted-foreground'}>
                    {route.name}
                  </span>
                  <ChevronRight
                    className={`ml-auto h-4 w-4 transition-transform duration-200 text-muted-foreground/50 group-data-[state=open]/collapsible:rotate-90 ${
                      hasActiveChild ? 'text-primary/50' : ''
                    }`}
                  />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {(route as GroupRoute).children.map((childRoute) => {
                    if ('path' in childRoute) {
                      const isActive = isRouteActive(childRoute.path);
                      const handleChildClick = () => {
                        if (childRoute.path.includes(':subjectId') && !subjectId) {
                          toast.error('Please select a subject first');
                          return;
                        }
                        const finalPath =
                          childRoute.path.includes(':subjectId') && subjectId
                            ? generatePath(childRoute.path, { subjectId })
                            : childRoute.path;
                        // if we are in the mobile view, we need to navigate to the final path and also close the sidebar
                        if (isMobile) {
                          setOpenMobile(false);
                        }
                        navigate(finalPath);
                      };

                      return (
                        <SidebarMenuSubItem key={`child-${childRoute.path}`}>
                          <SidebarMenuSubButton
                            className={`${hoverClassName} ${
                              isActive ? activeClassName : ''
                            } px-3 py-2 ml-2 text-[14px] cursor-pointer`}
                            onClick={handleChildClick}
                          >
                            <span className={isActive ? 'text-primary' : 'text-muted-foreground'}>
                              {childRoute.name}
                            </span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    }
                    return null;
                  })}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        );
      }
      return null;
    },
    [
      openGroups,
      isGroupActive,
      isRouteActive,
      toggleGroup,
      activeClassName,
      hoverClassName,
      subjectId,
      navigate,
    ]
  );

  const renderPageRoute = React.useCallback(
    (route: RouteConfig) => {
      if ('path' in route) {
        const isActive = isRouteActive((route as PageRoute).path);
        const path = (route as PageRoute).path;

        const handleClick = () => {
          if (path.includes(':subjectId') && !subjectId) {
            toast.error('Please select a subject first');
            return;
          }
          const finalPath =
            path.includes(':subjectId') && subjectId ? generatePath(path, { subjectId }) : path;
          if (isMobile) {
            setOpenMobile(false);
          }
          navigate(finalPath);
        };

        return (
          <SidebarMenuItem key={`page-${path}`}>
            <SidebarMenuButton
              className={`${hoverClassName} ${
                isActive ? activeClassName : ''
              } px-3 py-2 text-[15px] cursor-pointer`}
              onClick={handleClick}
            >
              {route.icon && (
                <route.icon
                  className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
                />
              )}
              <span className={isActive ? 'text-primary' : 'text-muted-foreground'}>
                {route.name}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }
      return null;
    },
    [isRouteActive, activeClassName, hoverClassName, subjectId, navigate]
  );

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <LogoHeader />
      </SidebarHeader>
      <SidebarContent className="overflow-y-auto no-scrollbar">
        {isLoading ? (
          <SidebarSkeleton />
        ) : (
          <SidebarMenu>
            {sidebarRoutes.map((route) => {
              if ('isLabel' in route) {
                return renderLabelRoute(route);
              } else if ('isGroup' in route) {
                return renderGroupRoute(route);
              } else if ('path' in route) {
                return renderPageRoute(route);
              }
              return null;
            })}
          </SidebarMenu>
        )}
        {/* <NavProjects projects={data.projects} /> */}
      </SidebarContent>
      {/* <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter> */}
      <SidebarRail />
    </Sidebar>
  );
}
